import { useEffect, useRef } from "react";
import classNames from "classnames/bind";
import styles from "./CardText.module.css";
import { Prose } from "../Prose/Prose";
import gsap from "gsap";
import SplitText from "gsap/SplitText";

gsap.registerPlugin(SplitText);

const cx = classNames.bind(styles);

export function CardText({
  className,
  kicker,
  title,
  text,
}: {
  className: string;
  kicker: string;
  title: string;
  text: string | React.ReactNode;
}) {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const title = titleRef.current;
    if (!title) return;

    // Wait for fonts to load before using SplitText
    const initAnimation = () => {
      const splitTitle = new SplitText(title, { type: "words" });
      gsap.fromTo(
        splitTitle.words,
        {
          x: 20,
          opacity: 0,
        },
        {
          duration: 1,
          x: 0,
          opacity: 1,
          delay: 0.8,
          ease: "power2.out",
          stagger: 0.05,
        },
      );
      gsap.fromTo(
        textRef.current,
        {
          y: 20,
          opacity: 0,
        },
        {
          duration: 1,
          y: 0,
          opacity: 1,
          delay: 0.8,
          ease: "power2.out",
        },
      );
    };

    // Check if fonts are loaded
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(initAnimation);
    } else {
      // Fallback for browsers without font loading API
      setTimeout(initAnimation, 100);
    }
  }, [kicker, title, text]);

  return (
    <div className={cx("container", className)}>
      <div className={cx("container_wrapper")}>
        <p className={cx("kicker")}>{kicker}</p>
        <h3 ref={titleRef} className={cx("title")}>
          {title}
        </h3>
        <div ref={textRef} className={cx("text_wrapper")}>
          <Prose className={cx("text")} size="base">
            {text}
          </Prose>
        </div>
      </div>
    </div>
  );
}
